import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { partnerDashboardService } from '../../../apis/partnerDashboard';
import { Ride, RideType, RideStatus } from '../../../types/ride';
import PartnerRideList from './PartnerRideList';
import StatusFilter from '../../Rides/components/StatusFilter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ride-tabpanel-${index}`}
      aria-labelledby={`ride-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface PartnerRidesProps {
  partnerId: string;
}

const rideTypes = [
  { id: RideType.NORMAL, label: 'Normal Rides' },
  { id: RideType.SHARED, label: 'Shared Rides' },
  { id: RideType.DELIVERY, label: 'Delivery Rides' },
];

const filterTypes = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Range' },
];

export default function PartnerRides({ partnerId }: PartnerRidesProps) {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    filter_type: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'custom',
    ride_type: rideTypes[0].id,
    ride_status: undefined as RideStatus | undefined,
    start_date: undefined as string | undefined,
    end_date: undefined as string | undefined,
    driver_id: undefined as string | undefined,
    car_id: undefined as string | undefined,
    owner_id: undefined as string | undefined,
  });
  const [rides, setRides] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 0,
  });
  const [drivers, setDrivers] = useState<any[]>([]);
  const [cars, setCars] = useState<any[]>([]);
  const [owners, setOwners] = useState<any[]>([]);

  const fetchRides = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const today = new Date();
      let startDate: Date | undefined;
      let endDate: Date | undefined;

      // Set default dates based on filter_type if not provided
      if (!filters.start_date || !filters.end_date) {
        switch (filters.filter_type) {
          case 'daily':
            startDate = startOfDay(today);
            endDate = endOfDay(today);
            break;
          case 'weekly':
            startDate = startOfWeek(today);
            endDate = endOfWeek(today);
            break;
          case 'monthly':
            startDate = startOfMonth(today);
            endDate = endOfMonth(today);
            break;
          case 'custom':
            // For custom, use provided dates or default to last 7 days
            startDate = filters.start_date ? new Date(filters.start_date) : startOfWeek(today);
            endDate = filters.end_date ? new Date(filters.end_date) : endOfWeek(today);
            break;
        }
      } else {
        startDate = new Date(filters.start_date);
        endDate = new Date(filters.end_date);
      }

      const response = await partnerDashboardService.getPartnerRides(partnerId, {
        page: filters.page,
        limit: filters.limit,
        ride_type: filters.ride_type,
        ride_status: filters.ride_status,
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        filter_type: filters.filter_type,
        driver_id: filters.driver_id,
        car_id: filters.car_id,
        owner_id: filters.owner_id,
      });
      
      setRides(response.rides);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load rides. Please try again later.');
      console.error('Error fetching rides:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
    fetchCars();
    fetchOwners();
  }, [partnerId]);

  useEffect(() => {
    fetchRides();
  }, [filters, selectedTab, partnerId]);

  const fetchDrivers = async () => {
    try {
      const driversData = await partnerDashboardService.getPartnerDrivers(partnerId);
      setDrivers(Array.isArray(driversData) ? driversData : []);
    } catch (error) {
      console.error("Failed to fetch drivers:", error);
    }
  };

  const fetchCars = async () => {
    try {
      const carsData = await partnerDashboardService.getPartnerCars(partnerId);
      setCars(Array.isArray(carsData) ? carsData : []);
    } catch (error) {
      console.error("Failed to fetch cars:", error);
    }
  };

  const fetchOwners = async () => {
    try {
      const ownersData = await partnerDashboardService.getOwners(partnerId);
      setOwners(Array.isArray(ownersData) ? ownersData : []);
    } catch (error) {
      console.error("Failed to fetch owners:", error);
    }
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setFilters(prev => ({ ...prev, page: 1, ride_type: rideTypes[newValue].id }));
  };

  const handleFilterTypeChange = (filterType: string) => {
    const today = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (filterType) {
      case 'daily':
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case 'weekly':
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case 'monthly':
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case 'custom':
        startDate = undefined;
        endDate = undefined;
        break;
    }

    handleFilterChange({
      filter_type: filterType as 'daily' | 'weekly' | 'monthly' | 'custom',
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    });
  };

  const handleDateRangeChange = (type: 'start' | 'end', date: Date | null) => {
    if (date) {
      handleFilterChange({
        [`${type}_date`]: format(date, 'yyyy-MM-dd'),
        filter_type: 'custom',
      });
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Transactions Management
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        View and manage all transactions from your partner drivers
      </Typography>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {rideTypes.map((type) => (
          <Tab key={type.id} label={type.label} />
        ))}
      </Tabs>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <ButtonGroup variant="outlined" size="small">
              {filterTypes.map((type) => (
                <Button
                  key={type.value}
                  onClick={() => handleFilterTypeChange(type.value)}
                  variant={filters.filter_type === type.value ? 'contained' : 'outlined'}
                >
                  {type.label}
                </Button>
              ))}
            </ButtonGroup>
          </Grid>

          {filters.filter_type === 'custom' && (
            <>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={filters.start_date ? new Date(filters.start_date) : null}
                    onChange={(date) => handleDateRangeChange('start', date)}
                    maxDate={filters.end_date ? new Date(filters.end_date) : undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={filters.end_date ? new Date(filters.end_date) : null}
                    onChange={(date) => handleDateRangeChange('end', date)}
                    minDate={filters.start_date ? new Date(filters.start_date) : undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <StatusFilter
              status={filters.ride_status}
              onStatusChange={(status) => handleFilterChange({ ride_status: status })}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Driver</InputLabel>
              <Select
                value={filters.driver_id || ''}
                onChange={(e) => handleFilterChange({ driver_id: e.target.value || undefined })}
                label="Filter by Driver"
              >
                <MenuItem value="">All Drivers</MenuItem>
                {drivers
                  .filter((d) => !d.isPending)
                  .map((driver) => {
                    const displayName = `${driver.firstName || ""} ${driver.lastName || ""}`.trim() || driver.email;
                    return (
                      <MenuItem key={driver._id} value={driver._id}>
                        {displayName}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Car</InputLabel>
              <Select
                value={filters.car_id || ''}
                onChange={(e) => handleFilterChange({ car_id: e.target.value || undefined })}
                label="Filter by Car"
              >
                <MenuItem value="">All Cars</MenuItem>
                {cars.map((car) => (
                  <MenuItem key={car._id} value={car._id}>
                    {car.car_make} {car.car_model} - {car.license_plate}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Owner</InputLabel>
              <Select
                value={filters.owner_id || ''}
                onChange={(e) => handleFilterChange({ owner_id: e.target.value || undefined })}
                label="Filter by Owner"
              >
                <MenuItem value="">All Owners</MenuItem>
                {owners.map((owner) => (
                  <MenuItem key={owner._id} value={owner._id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        rideTypes.map((type, index) => {
          // Apply client-side filtering for all filters
          // (Backend accepts parameters but filtering is done here for reliability)
          let filteredRides = rides;
          
          if (filters.driver_id) {
            filteredRides = filteredRides.filter((ride) => ride.driver?.driver_id === filters.driver_id);
          }
          
          if (filters.car_id) {
            filteredRides = filteredRides.filter((ride) => {
              const rideCarId = ride.car?.car_id;
              return rideCarId && (rideCarId === filters.car_id || String(rideCarId) === String(filters.car_id));
            });
          }
          
          if (filters.owner_id) {
            // Filter by owner - need to match car owner_id
            filteredRides = filteredRides.filter((ride) => {
              if (!ride.car?.car_id) return false;
              // Match car_id from ride with _id from cars list
              const car = cars.find((c) => {
                const carId = String(c._id);
                const rideCarId = String(ride.car?.car_id);
                return carId === rideCarId;
              });
              return car?.owner_id === filters.owner_id || String(car?.owner_id) === String(filters.owner_id);
            });
          }

          // Enrich rides with owner names from cars
          const enrichedRides = filteredRides.map((ride) => {
            if (ride.car?.car_id) {
              // Match car_id from ride with _id from cars list
              const car = cars.find((c) => {
                const carId = String(c._id);
                const rideCarId = String(ride.car?.car_id);
                return carId === rideCarId;
              });
              if (car && car.owner_name) {
                return {
                  ...ride,
                  car: {
                    ...ride.car,
                    owner_name: car.owner_name,
                  },
                };
              }
            }
            return ride;
          });

          return (
            <TabPanel key={type.id} value={selectedTab} index={index}>
              <PartnerRideList
                rides={enrichedRides}
                pagination={pagination}
                onPageChange={handlePageChange}
                onRideClick={(rideId) => navigate(`/rides/${rideId}`)}
              />
            </TabPanel>
          );
        })
      )}
    </Box>
  );
}

